import React, { useState, useEffect } from "react";
import { X, Info } from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import frameBg from "../../Assets/FrameBGDashboard.png";
import {
  getProjectName,
  CreateExternalTicket,
} from "../../Services/ReportIssuesService";
import NotificationAlert from "../Alerts/NotificationAlert";
import { GetCurrentUser } from "../../Services/LoginService";

const ReportIssue = () => {
  const { projectUID } = useParams();
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email");

  const navigate = useNavigate();

  const [newTicket, setNewTicket] = useState({
    Title: "",
    Descritption: "",
    TicketType: 1,
    Severity: 1,
    ProjectUID: "",
  });

  const [attachments, setAttachments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [alert, setAlert] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [projectName, setProjectName] = useState("");
  const [createdByEmail, setCreatedByEmail] = useState("");

  /* CLOSE HANDLER*/
  const handleClose = () => {
    navigate(-1);
  };

  useEffect(() => {
    if (projectUID) {
      fetchProjectName(projectUID);

      setNewTicket((prev) => ({
        ...prev,
        ProjectUID: projectUID,
      }));
    }
  }, [projectUID]);

  const fetchProjectName = async (uid) => {
    try {
      const res = await getProjectName(uid);

      if (res.data?.status === "Fail" || res.data?.status === "Success") {
        setProjectName(res.data.projectName || "");
      }
    } catch (err) {
      setAlert({
        type: "error",
        message: "Unable to fetch project name",
      });
    }
  };

  useEffect(() => {
    if (email) {
      setCreatedByEmail(email);
    }
  }, [email]);

  useEffect(() => {
    // Public Report-Issue page → DO NOT call GetCurrentUser
    if (window.location.pathname.startsWith("/Report-Issue")) {
      return;
    }

    const fetchCurrentUser = async () => {
      try {
        const res = await GetCurrentUser();
        if (res?.status === "Success") {
          setCreatedByEmail(res.data.email);
        }
      } catch {
        // silently ignore
      }
    };

    fetchCurrentUser();
  }, []);

  /* INPUT HANDLING  */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewTicket((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const err = {};
    if (!newTicket.Title.trim()) err.Title = "Title is required";
    if (!newTicket.Descritption.trim())
      err.Descritption = "Description is required";
    if (!newTicket.TicketType) err.TicketType = "Ticket Type is required";
    if (!newTicket.Severity) err.Severity = "Severity is required";
    if (!newTicket.ProjectUID) err.ProjectUID = "Project is required";
    return err;
  };

  /* FILE HANDLING  */
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);

    // TOTAL FILE COUNT CHECK (MAX 5)
    const totalFiles = attachments.length + files.length;
    if (totalFiles > 5) {
      setAlert({
        type: "error",
        message: "You can upload a maximum of 5 attachments.",
      });
      e.target.value = "";
      return;
    }

    // Allowed MIME types
    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];

    // Backend size limits
    const sizeLimits = {
      "image/jpeg": 200 * 1024,
      "image/png": 200 * 1024,
      "application/pdf": 5 * 1024 * 1024,
      "application/msword": 2 * 1024 * 1024,
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        2 * 1024 * 1024,
      "application/vnd.ms-excel": 2 * 1024 * 1024,
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
        2 * 1024 * 1024,
    };

    for (let file of files) {
      if (
        !allowedTypes.includes(file.type) ||
        file.size > sizeLimits[file.type]
      ) {
        setAlert({
          type: "error",
          message:
            "Invalid attachment. Allowed file types and sizes: Image(JPEG, PNG) ≤ 200 KB, Word ≤ 2 MB, Excel ≤ 2MB, PDF ≤ 5 MB.",
        });
        e.target.value = "";
        return;
      }
    }

    setAttachments((prev) => [...prev, ...files]);
    e.target.value = "";
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  /*SUBMIT */
  const handleAddTicket = async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }

    try {
      setLoading(true);

      if (!createdByEmail) {
        setAlert({
          type: "error",
          message: "User email not found. Please login again.",
        });
        setLoading(false);
        return;
      }
      const formData = new FormData();

      Object.keys(newTicket).forEach((key) =>
        formData.append(key, newTicket[key])
      );
      formData.append("CreatedByEmail", createdByEmail);

      attachments.forEach((file) => formData.append("Attchement", file));

      const response = await CreateExternalTicket(formData);

      if (response.data?.status === "Success") {
        setAlert({
          type: "success",
          message: response.data?.message || "Ticket created successfully",
        });
        setTimeout(() => handleClose(), 2000);
      } else {
        setAlert({
          type: "error",
          message: response.data?.message || "Failed to create ticket",
        });
      }
    } catch (error) {
      setAlert({
        type: "error",
        message: error.response?.data?.message || "Server error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="w-full bg-white flex flex-col">
        {/* Header */}
        <div
          className="flex justify-between items-center px-6 py-4 border-b text-white shadow-md bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${frameBg})` }}
        >
          <h2 className="text-2xl font-semibold">Report Issue</h2>
          <button
            onClick={() => navigate(-1)}
            className="text-white hover:opacity-80"
          >
            <X size={26} />
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto px-8 py-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Title */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="Title"
                value={newTicket.Title}
                onChange={handleInputChange}
                className={`w-full p-2 border rounded-md mt-1 text-sm ${
                  errors.Title ? "border-red-500" : ""
                }`}
                placeholder="Enter ticket title"
              />
              {errors.Title && (
                <p className="text-xs text-red-500 mt-1">{errors.Title}</p>
              )}
            </div>

            {/* Description */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                name="Descritption"
                value={newTicket.Descritption}
                onChange={handleInputChange}
                rows={4}
                className={`w-full p-2 border rounded-md mt-1 text-sm ${
                  errors.Descritption ? "border-red-500" : ""
                }`}
                placeholder="Enter ticket description"
              />
              {errors.Descritption && (
                <p className="text-xs text-red-500 mt-1">
                  {errors.Descritption}
                </p>
              )}
            </div>

            {/* Ticket Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Ticket Type <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value="Bug"
                disabled
                className="w-full p-2 border rounded-md mt-1 text-sm bg-gray-100 cursor-not-allowed"
              />
              {errors.TicketType && (
                <p className="text-xs text-red-500 mt-1">{errors.TicketType}</p>
              )}
            </div>

            {/* Severity */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Severity <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value="High"
                disabled
                className="w-full p-2 border rounded-md mt-1 text-sm bg-gray-100 cursor-not-allowed"
              />
              {errors.Severity && (
                <p className="text-xs text-red-500 mt-1">{errors.Severity}</p>
              )}
            </div>

            {/* Project */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Project Name <span className="text-red-500">*</span>
              </label>

              <input
                type="text"
                value={projectName}
                disabled
                className="w-full p-2 border rounded-md mt-1 text-sm bg-gray-100 cursor-not-allowed"
              />

              {errors.ProjectUID && (
                <p className="text-xs text-red-500 mt-1">{errors.ProjectUID}</p>
              )}
            </div>

            {/* Attachment */}
            <div>
              <div className="flex items-center gap-2">
                <label className="block text-sm font-medium text-gray-700">
                  Attachment
                </label>

                {/* Info Icon with Tooltip */}
                <div className="relative group">
                  <Info
                    size={16}
                    className="text-gray-500 cursor-pointer hover:text-gray-700"
                  />
                </div>
              </div>

              <input
                type="file"
                name="Attchement"
                onChange={handleFileChange}
                accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx"
                multiple
                className="w-full p-2 border rounded-md mt-1 text-sm"
              />
            </div>

            {/* Attachment Preview List */}
            {attachments.length > 0 && (
              <div className="col-span-2 mt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">
                  Attachments ({attachments.length})
                </h3>

                <div className="w-full bg-gray-50 border rounded-lg p-4 max-h-48 overflow-y-auto">
                  {attachments.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 bg-white rounded-md shadow-sm border mb-2"
                    >
                      {/* File Preview */}
                      <div className="flex items-center gap-3">
                        {file.type.startsWith("image/") ? (
                          <img
                            src={URL.createObjectURL(file)}
                            className="w-12 h-12 rounded object-cover border"
                            alt="preview"
                          />
                        ) : (
                          <div className="w-12 h-12 flex items-center justify-center bg-gray-200 rounded text-gray-600 text-sm">
                            {file.name.split(".").pop().toUpperCase()}
                          </div>
                        )}

                        {/* File Info */}
                        <div>
                          <p className="text-sm font-medium text-gray-800">
                            {file.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {(file.size / 1024).toFixed(1)} KB
                          </p>
                        </div>
                      </div>

                      {/* Remove */}
                      <button
                        onClick={() => removeAttachment(index)}
                        className="text-red-500 hover:text-red-700 text-sm font-medium"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-white flex flex-col sm:flex-row justify-end gap-3">
          <button
            onClick={handleClose}
            className="px-5 py-2 text-sm border rounded-md text-gray-600 hover:bg-gray-100"
            disabled={loading}
          >
            Cancel
          </button>

          <button
            onClick={handleAddTicket}
            disabled={loading}
            className="px-6 py-2 text-sm bg-gradient-to-r from-[#0061A4] to-[#00ACED]
                     text-white rounded-md shadow hover:opacity-90"
          >
            {loading ? "Creating..." : "Report Issue"}
          </button>
        </div>
      </div>

      {alert && (
        <NotificationAlert
          type={alert.type}
          message={alert.message}
          show={true}
          duration={4000}
          onClose={() => setAlert(null)}
        />
      )}
    </div>
  );
};

export default ReportIssue;
